import React, { useState, useRef } from "react";
import Chart from "chart.js/auto";
import "../assets/styles/compound-interest.css";

const CompoundInterest = () => {
  const [inputs, setInputs] = useState({
    principal: "",
    annualContribution: "",
    rate: "",
    time: "",
    compounding: "1",
  });

  const [result, setResult] = useState("");
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);

  // Format number with commas
  const formatNumberWithCommas = (value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Remove commas from the input
  const removeCommas = (value) => {
    return value.replace(/,/g, "");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const cleanedValue = value.replace(/[^0-9.]/g, ""); // Allow only numbers and dots
    const formattedValue = formatNumberWithCommas(cleanedValue);
    setInputs((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const calculateCompoundInterest = () => {
    const principal = parseFloat(removeCommas(inputs.principal) || 0);
    const annualContribution = parseFloat(removeCommas(inputs.annualContribution) || 0);
    const rate = parseFloat(inputs.rate || 0) / 100;
    const time = parseInt(inputs.time || 0, 10);
    const compounding = parseInt(inputs.compounding, 10);

    if (isNaN(principal) || isNaN(annualContribution) || isNaN(rate) || isNaN(time) || isNaN(compounding)) {
      setResult("Please enter valid inputs.");
      return;
    }

    const labels = Array.from({ length: time }, (_, i) => `Year ${i + 1}`);
    const contributionData = [];
    const interestData = [];
    let cumulativeContributions = 0;

    let futureValue = 0;

    for (let year = 1; year <= time; year++) {
      const yearPrincipalValue = principal * Math.pow(1 + rate / compounding, compounding * year);
      cumulativeContributions += annualContribution;

      const yearContributionValue = rate > 0
        ? (annualContribution * (Math.pow(1 + rate / compounding, compounding * year) - 1)) / (rate / compounding)
        : cumulativeContributions;

      futureValue = yearPrincipalValue + yearContributionValue;
      contributionData.push(cumulativeContributions + principal);
      interestData.push(Math.round(futureValue - (cumulativeContributions + principal)));
    }

    setResult(`Future Value: $${Math.round(futureValue).toLocaleString()}`);

    if (chartInstance) chartInstance.destroy();

    const ctx = chartRef.current.getContext("2d");
    setChartInstance(
      new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            { label: "Contributions", data: contributionData, backgroundColor: "#95D1FC" },
            { label: "Interest", data: interestData, backgroundColor: "#87C19F" },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            title: { display: true, text: "Compound Interest Growth Over Time" },
          },
          scales: {
            y: { stacked: true, title: { display: true, text: "Amount ($)" } },
            x: { stacked: true, title: { display: true, text: "Years" } },
          },
        },
      })
    );
  };

  return (
    <div className="calculator-container">
      <section className="calculator-hero">
        <h1>Compound Interest Calculator</h1>
        <p>
          Calculate how your savings grow over time with compound interest.
          Just fill out the fields below to get started.
        </p>
      </section>

      <form className="calculator-form" onSubmit={(e) => e.preventDefault()}>
        <label htmlFor="principal">Current Principal ($):</label>
        <input
          type="text"
          id="principal"
          name="principal"
          value={inputs.principal}
          onChange={handleInputChange}
          placeholder="Enter principal amount"
          required
        />

        <label htmlFor="annualContribution">Annual Contribution ($):</label>
        <input
          type="text"
          id="annualContribution"
          name="annualContribution"
          value={inputs.annualContribution}
          onChange={handleInputChange}
          placeholder="Enter annual contribution amount"
          required
        />

        <label htmlFor="rate">Annual Interest Rate (%):</label>
        <input
          type="text"
          id="rate"
          name="rate"
          value={inputs.rate}
          onChange={handleInputChange}
          placeholder="Enter interest rate"
          required
        />

        <label htmlFor="time">Time (Years):</label>
        <input
          type="text"
          id="time"
          name="time"
          value={inputs.time}
          onChange={handleInputChange}
          placeholder="Enter number of years"
          required
        />

        <label htmlFor="compounding">Compounding Frequency:</label>
        <select
          id="compounding"
          name="compounding"
          value={inputs.compounding}
          onChange={(e) => handleInputChange(e)}
        >
          <option value="1">Yearly</option>
          <option value="4">Quarterly</option>
          <option value="12">Monthly</option>
          <option value="365">Daily</option>
        </select>

        <button type="button" onClick={calculateCompoundInterest} className="calculate-button">
          Calculate
        </button>
      </form>

      <p className="result">{result}</p>

      <div className="chart-container">
        <canvas id="compoundChart" ref={chartRef}></canvas>
      </div>
    </div>
  );
};

export default CompoundInterest;
